/**
 * <MagneticButton /> — wrapper that gives a child element a subtle "magnetic"
 * pull toward the cursor. The element drifts up to `strength` pixels in
 * the direction of the pointer when the cursor is over it.
 *
 * v34.2 (Mar 2026): used on the two primary hero CTAs (Try Live Demo,
 * Run AI Cost Calculator). Tactile, premium feel — Apple / Linear pattern.
 *
 * Notes:
 *   - Respects `prefers-reduced-motion`.
 *   - Falls back to no-op on touch devices (pointermove-with-mouse only).
 *   - Re-uses the child element exactly — no extra DOM wrappers.
 */
import React, { useEffect, useRef } from 'react';

const MagneticButton = ({ children, strength = 14, className = '' }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    // Touch-only devices: skip
    if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) {
      return undefined;
    }

    const inner = el.firstElementChild;
    if (!inner) return undefined;

    let rafId = null;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      const dx = (x / rect.width) * strength * 2;
      const dy = (y / rect.height) * strength * 2;
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          inner.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
          rafId = null;
        });
      }
    };

    const onLeave = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      inner.style.transform = 'translate3d(0, 0, 0)';
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    inner.style.transition = 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)';
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [strength]);

  return (
    <span ref={ref} className={`inline-block ${className}`}>
      {children}
    </span>
  );
};

export default MagneticButton;
